import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Typography } from 'components/index';
import IconTooltip from '../common/IconTooltip';
import { formatYAxis } from './charts.util';

export interface GraphData {
  location?: string;
  projectType?: string;
  avgValue: number;
}

export interface CCReportSingleViewBarChartProps {
  dataByLocation: GraphData[];
  dataByType: GraphData[];
  openDetailedView?: () => void;
}

// Custom tick component for multi-line X-axis labels with smaller font size
const MultiLineXAxisTick: React.FC<any> = ({ x, y, payload }) => {
  const label = payload.value;
  const words = label.split(' '); // Split label into words for multi-line wrapping
  const maxCharsPerLine = 10; // Adjust this value based on the width

  // Break the label into lines if it exceeds maxCharsPerLine
  const lines = words.reduce((acc: string[], word: any) => {
    const lastLine = acc[acc.length - 1];
    if (lastLine && lastLine.length + word.length <= maxCharsPerLine) {
      acc[acc.length - 1] = lastLine + ' ' + word;
    } else {
      acc.push(word);
    }
    return acc;
  }, []);

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line: any, index: any) => (
        <text key={index} x={0} y={index * 12} dy={12} textAnchor='middle' fill='#666' style={{ fontSize: '10px' }}>
          {line}
        </text>
      ))}
    </g>
  );
};

// Custom Tooltip
const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const { value } = payload[0];
    const roundedValue = value.toFixed(2);

    return (
      <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '8px', fontSize: '12px' }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p> {/* Show location or projectType */}
        <p style={{ margin: 0 }}>{`$ ${roundedValue}`}</p> {/* Display the rounded avgValue */}
      </div>
    );
  }

  return null;
};

const CCReportSingleViewBarChart: React.FC<CCReportSingleViewBarChartProps> = ({ dataByLocation, dataByType, openDetailedView }) => {
  const renderLocationsOnly = dataByType.length <= 1;
  const renderProjectTypesOnly = dataByLocation.length <= 1;
  const hideToggleButtons = renderLocationsOnly || renderProjectTypesOnly;
  const [view, setView] = useState<'location' | 'type'>(renderProjectTypesOnly ? 'type' : 'location');

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newValue: 'location' | 'type' | null) => {
    if (newValue != null) setView(newValue);
  };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '10px',
          marginTop: '20px',
          marginRight: 30,
          marginLeft: 50,
        }}
      >
        {hideToggleButtons ? null : (
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleViewChange}
            aria-label='chart type selector'
            size='small'
            color='primary'
          >
            <ToggleButton value='location' aria-label='location' title='Locations' disabled={renderProjectTypesOnly}>
              <Typography variant='caption' fontWeight='medium'>
                Locations
              </Typography>
            </ToggleButton>
            <ToggleButton value='type' aria-label='project type' title='Project Types' disabled={renderLocationsOnly}>
              <Typography variant='caption' fontWeight='medium'>
                Project Types
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        <IconButton onClick={openDetailedView} title='Open Detailed View' sx={{ marginLeft: 'auto' }}>
          <IconTooltip icon='fullscreen' title='Open Detailed View' fontSize='medium' />
        </IconButton>
      </div>
      {view === 'location' ? renderChart(dataByLocation, 'location') : renderChart(dataByType, 'type')}
    </div>
  );
};

const renderChart = (graphData: GraphData[], view: 'location' | 'type', marginTop?: number) => {
  // Add color to each item based on the location or project type
  const dataWithColors = graphData.map((entry, index) => ({
    ...entry,
    fill: '#4E79A7',
  }));

  return (
    <div style={{ width: '100%', height: '400px', marginTop: marginTop }}>
      <ResponsiveContainer width='100%' height='100%'>
        <BarChart data={dataWithColors} margin={{ top: 20, right: 30, left: 10, bottom: 40 }}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis
            dataKey={view === 'location' ? 'location' : 'projectType'}
            tick={<MultiLineXAxisTick />}
            interval={0} //ensures display of all labels, no skipping
          />
          <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 10 }} />
          <Tooltip content={<CustomTooltip />} />
          {/* Render the bars with assigned colors */}
          <Bar dataKey='avgValue' fill={undefined} barSize={30}>
            {dataWithColors.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CCReportSingleViewBarChart;
